import React, { useEffect, useState } from 'react';
import { Button, Modal } from 'antd';
import { CloseOutlined, MoreOutlined, PlusSquareOutlined } from '@ant-design/icons';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { useInstallApp } from '../../lib/installApp';
import { useAndroidWelcome } from '../../lib/androidWelcome';
import { shouldOfferInstall } from '../../shared/utils/install-app';
import { useAppSelector } from '../../store/hooks';
import { selectUser } from '../../store/slices/userSlice';

// Pages with their own bottom action dock or a focused auth form stay uncluttered.
const QUIET_ROUTES = [/^\/oyna\/\[id\]/, /^\/uyelik\//];

function ShareGlyph() {
  // Safari's share symbol, so the step matches what the person sees on screen.
  return (
    <svg viewBox="0 0 24 24" width="1em" height="1em" aria-hidden="true" focusable="false" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3v12M8 7l4-4 4 4" />
      <path d="M8 11H6a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-8a1 1 0 0 0-1-1h-2" />
    </svg>
  );
}

export default function InstallApp() {
  const { t } = useTranslation();
  const router = useRouter();
  const user = useAppSelector(selectUser);
  const { path, visits, dismissedAt, guideOpen, install, dismiss, closeGuide } = useInstallApp();
  const [isPhone, setIsPhone] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const media = window.matchMedia('(max-width: 767px)');
    const sync = () => setIsPhone(media.matches);
    sync();
    media.addEventListener?.('change', sync);
    return () => media.removeEventListener?.('change', sync);
  }, []);

  const androidWelcome = useAndroidWelcome();
  // Android karşılaması açıkken ana ekrana ekleme oradan önerilir.
  const quiet = androidWelcome.active || QUIET_ROUTES.some(route => route.test(router.pathname));
  const offer = !quiet && shouldOfferInstall({ path, isPhone, visits, signedIn: !!user?.uid, dismissedAt });

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}${window.location.pathname}`);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const steps: Array<{ icon: React.ReactNode; text: string }> = path === 'android-manual'
    ? [
      { icon: <MoreOutlined aria-hidden="true" />, text: t('installApp.steps.androidMenu') },
      { icon: <PlusSquareOutlined aria-hidden="true" />, text: t('installApp.steps.androidAdd') },
    ]
    : [
      ...(path === 'in-app' ? [{ icon: <MoreOutlined aria-hidden="true" />, text: t('installApp.steps.openInBrowser') }] : []),
      { icon: <ShareGlyph />, text: t(path === 'ios-browser' ? 'installApp.steps.shareBrowser' : 'installApp.steps.shareSafari') },
      { icon: <PlusSquareOutlined aria-hidden="true" />, text: t('installApp.steps.addToHome') },
      { icon: <span className="install-guide-step-add" aria-hidden="true">{t('installApp.steps.addLabel')}</span>, text: t('installApp.steps.confirm') },
    ];

  return (
    <>
      {offer && (
        <aside className="install-banner" aria-label={t('installApp.title')}>
          <img src="/images/apple-touch-icon.png" alt="" width={44} height={44} />
          <div className="install-banner-text">
            <strong>{t('installApp.title')}</strong>
            <span>{t(path === 'native' ? 'installApp.subtitleNative' : 'installApp.subtitle')}</span>
          </div>
          <Button type="primary" onClick={install}>
            {t(path === 'native' ? 'installApp.install' : 'installApp.how')}
          </Button>
          <button type="button" className="install-banner-close" onClick={dismiss} aria-label={t('installApp.later')}>
            <CloseOutlined aria-hidden="true" />
          </button>
        </aside>
      )}

      <Modal
        className="install-guide"
        title={t('installApp.guideTitle')}
        visible={guideOpen}
        footer={null}
        onCancel={closeGuide}
        afterClose={() => setCopied(false)}
        destroyOnClose
      >
        {path === 'in-app' && <p className="install-guide-note">{t('installApp.inAppNote')}</p>}
        <ol className="install-guide-steps">
          {steps.map((step, index) => (
            // eslint-disable-next-line react/no-array-index-key -- static, ordered steps
            <li key={index}>
              <span className="install-guide-step-icon">{step.icon}</span>
              <span>{step.text}</span>
            </li>
          ))}
        </ol>
        {path !== 'android-manual' && <p className="install-guide-note">{t('installApp.missingOption')}</p>}
        <div className="install-guide-actions">
          <Button onClick={copyLink}>{t(copied ? 'installApp.copied' : 'installApp.copyLink')}</Button>
          <Button type="primary" onClick={closeGuide}>{t('installApp.done')}</Button>
        </div>
        <p className="visually-hidden" role="status" aria-live="polite">{copied ? t('installApp.copied') : ''}</p>
      </Modal>
    </>
  );
}
